import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import Flow from '@flowjs/flow.js';
import {CookieService} from 'ngx-cookie-service';
import {BehaviorSubject} from 'rxjs';
import {FileUploadService} from 'src/app/file-upload.service';

interface FlowEvent {
  type: string;
  event: any[];
}

@Component({
    selector: 'app-file-input',
    templateUrl: './app-file-input.component.html',
    styleUrls: ['./app-file-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: AppFileInputComponent,
            multi: true,
        },
    ],
    standalone: false
})
export class AppFileInputComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  constructor(
    private _fileService: FileUploadService,
    private cookieService: CookieService,
  ) {
    const csrfToken = this.cookieService.get('csrftoken');
    this.headers = {
      'X-Csrftoken': csrfToken,
    };
  }

  @ViewChild('dropArea') dropArea!: ElementRef<HTMLDivElement>;
  @ViewChild('browseInput') browseInput!: ElementRef<HTMLInputElement>;

  @Output() fileAdded = new EventEmitter<any>();
  @Output() fileSuccessfullyAdded = new EventEmitter<any>();
  @Output() fileRemoved = new EventEmitter<string>();
  @Output() fileType = new EventEmitter<string>();

  @Input() fileUploading = false;
  @Input() required: boolean = false;
  @Input() title: string = '';
  @Input() multiple: boolean = false;
  @Input() description: string = '';
  @Input() fileTypes: string[] = ['.csv', '.pdf'];
  @Input() fileSize: number; //in MB
  @Input() maxDimensions: Dimensions;
  @Input() minDimensions: Dimensions;
  @Input() exactDimensions: Dimensions;
  @Input() orientation: Orientation;
  @Input() square = false;
  @Input() ratio: string;
  @Input() checkOnFileAdded = false;
  @Input() emitFileType = false;
  @Input() formControlNameInput: string;
  @Input() showRemoveBtn: boolean = true;

  flow: any;
  transfers$ = new BehaviorSubject<{transfers: any[]}>({transfers: []});

  files: File[];
  existingFiles = [];
  headers = {};
  uploadUrl = 'flow-js/upload/';
  allowUpload = true;
  fileDimensions: Dimensions;
  touched = false;
  disabled = false;

  private static readonly FLOW_EVENT_NAMES = [
    'fileAdded',
    'filesAdded',
    'filesSubmitted',
    'fileSuccess',
    'fileError',
    'fileRemoved',
    'fileProgress',
    'progress',
    'complete',
  ];

  ngAfterViewInit() {
    this.flow = new Flow({
      target: this.uploadUrl,
      testChunks: false,
      singleFile: !this.multiple,
      headers: this.headers,
      withCredentials: true,
    });
    this.flow.assignDrop(this.dropArea.nativeElement);
    this.flow.assignBrowse(this.browseInput.nativeElement);

    AppFileInputComponent.FLOW_EVENT_NAMES.forEach(name => {
      this.flow.on(name, (...args: any[]) => {
        this.refreshTransfers();
        this.handleFlowEvent({type: name, event: args});
      });
    });
  }

  private refreshTransfers() {
    this.transfers$.next({transfers: this.flow.files.slice()});
  }

  private handleFlowEvent(event: FlowEvent) {
    if (
      (event.type === 'fileAdded' || event.type === 'filesAdded') &&
      event?.event?.[0]?.file &&
      this.checkIfImage(event?.event?.[0]?.file.type)
    ) {
      this.getFileDimensions(event.event[0].file)
        .then(dim => {
          this.fileDimensions = dim;
          this.allowUpload = this.handleAddedFile(event, dim);
          this.emitFileState(event);
        })
        .catch(error => {
          console.error('Error:', error);
        });
    } else {
      this.allowUpload = this.handleAddedFile(event);
      this.emitFileState(event);
    }
  }

  emitFileState(event) {
    if (event && event.type === 'fileError') {
      const payload = this.formControlNameInput
        ? {formControlName: this.formControlNameInput, error: 'file-upload-error'}
        : 'file-upload-error';
      this.fileSuccessfullyAdded.emit(payload);
    } else if (event && event.type === 'fileSuccess' && this.allowUpload) {
      const e = event as any;
      const payload = this.formControlNameInput
        ? {formControlName: this.formControlNameInput, uploadedFileId: e.event[1]}
        : e.event[1];

      this.fileSuccessfullyAdded.emit(payload);
    } else if (event && this.allowUpload) {
      const e = event as any;
      if (e.event && event.type !== 'fileRemoved') {
        const payload = this.formControlNameInput
          ? {formControlName: this.formControlNameInput, event: e.event}
          : e.event;

        this.fileAdded.emit(payload);
      }
      if (event.type === 'fileRemoved') {
        const payload = this.formControlNameInput
          ? {formControlName: this.formControlNameInput, event: e.event}
          : e.event;

        this.fileRemoved.emit(payload);
      }
    }
  }



  handleAddedFile(event, fileDimensions?) {
    if (
      (this.checkOnFileAdded && (event.event || fileDimensions)) ||
      (event.type === 'filesSubmitted' && (event.event || fileDimensions))
    ) {
      let file;
      if (event.type === 'filesSubmitted' || event.type === 'filesAdded') {
        file = event?.event?.[0][0]?.file;
      } else {
        file = event?.event?.[0]?.file;
      }

      if (file) {
        if ((file.name.match(/\./g) || []).length > 1) {
          this.fileSuccessfullyAdded.emit('invalid-filename');
          this.cancelAllFiles();
          return false;
        }

        if (this.fileTypes && this.fileTypes.length > 0) {
          if (this.fileTypes.includes('.jpg')) {
            if (!this.fileTypes.includes('.jpeg')) {
              this.fileTypes.push('.jpeg');
            }
          }

          const fileType = file.type.substring(file.type.lastIndexOf('/') + 1);

          if (this.emitFileType) {
            this.fileType.emit(fileType);
          }
          if (!this.fileTypes.includes('.' + fileType)) {
            this.fileSuccessfullyAdded.emit('wrong-file-type');
            return this.fileTypes.includes('.' + fileType);
          }
        }

        const fileSizeMB = file.size / (1024 * 1024);
        if (this.fileSize && fileSizeMB > this.fileSize) {
          this.fileSuccessfullyAdded.emit('file-size-exceeded');
          return false;
        }
      }
      if (fileDimensions) {
        if (this.exactDimensions && this.exactDimensions.width && this.exactDimensions.height) {
          if (
            fileDimensions.width !== this.exactDimensions.width ||
            fileDimensions.height !== this.exactDimensions.height
          ) {
            this.fileDimensions = null;
            this.fileSuccessfullyAdded.emit('file-exact-dimensions-not-meet');
            return false;
          }
        }

        if (this.maxDimensions && this.maxDimensions.width && this.maxDimensions.height) {
          if (fileDimensions.width > this.maxDimensions.width || fileDimensions.height > this.maxDimensions.height) {
            this.fileDimensions = null;
            this.fileSuccessfullyAdded.emit('file-dimensions-not-meet');
            return false;
          }
        }

        if (this.minDimensions && this.minDimensions.width && this.minDimensions.height) {
          if (fileDimensions.width < this.minDimensions.width || fileDimensions.height < this.minDimensions.height) {
            this.fileDimensions = null;
            this.fileSuccessfullyAdded.emit('file-dimensions-not-meet');
            return false;
          }
        }

        if (this.orientation === 'horizontal' || this.orientation === 'vertical') {
          const orientation = this.getImageOrientation(fileDimensions);
          if (orientation !== this.orientation) {
            this.fileSuccessfullyAdded.emit('file-orientation-not-meet');
            return false;
          }
        }

        if (this.square) {
          if (!(fileDimensions.width === fileDimensions.height)) {
            this.fileSuccessfullyAdded.emit('file-not-square');
            return false;
          }
        }

        if (this.ratio) {
          if (!this.hasAspectRatio(fileDimensions.width, fileDimensions.height, this.ratio)) {
            this.fileSuccessfullyAdded.emit('file-has-not-aspect-ratio');
            return false;
          }
        }
      }

      return true;
    } else if (event.type === 'fileError' || event.type === 'fileSuccess' || event.type === 'fileRemoved') {
      return true;
    } else {
      return false;
    }
  }

  checkIfImage(type: string): boolean {
    return type.startsWith('image/');
  }

  hasAspectRatio(imageWidth: number, imageHeight: number, aspectRatioString: string): boolean {
    const aspectRatio = this.parseAspectRatio(aspectRatioString);
    const imageAspectRatio = imageWidth / imageHeight;
    return Math.abs(imageAspectRatio - aspectRatio) < 0.0001; // Adjust the tolerance as needed
  }

  parseAspectRatio(aspectRatioString: string): number {
    const [numerator, denominator] = aspectRatioString.split('/').map(Number);
    return numerator / denominator;
  }

  getImageOrientation(dimensions: Dimensions): 'vertical' | 'horizontal' {
    if (dimensions.width > dimensions.height) {
      return 'horizontal';
    } else {
      return 'vertical';
    }
  }

  getFileDimensions(file): Promise<Dimensions> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          resolve({width: width, height: height});
        };
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        img.src = e.target.result;
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(file);
    });
  }

  uploadFlow() {
    this.flow.upload();
  }

  uploadHandler(uploadedFiles: File[]) {
    for (let index in uploadedFiles) {
      if (this.files.filter(e => e.name === uploadedFiles[index].name).length === 0) {
        this.files.push(uploadedFiles[index]);
      } else {
        this.existingFiles.push(uploadedFiles[index].name);
      }
    }
  }

  onChange = files => {};
  onTouched = () => {};

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  valueChanged() {
    this.markAsTouched();
    if (!this.disabled) {
      this.onChange(this.files);
    }
  }

  writeValue(files: File[]) {
    this.files = files;
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  upload(filesLocation: string) {
    this._fileService.upload(new Set(this.files), filesLocation);
  }

  deleteFile(indexOfFile: number) {
    this.files.splice(indexOfFile, 1);
    this.valueChanged();
  }

  cancelFile(transfer: any) {
    if (this.flow && transfer) {
      this.flow.removeFile(transfer);
      this.refreshTransfers();
    }
  }

  cancelAllFiles() {
    if (this.flow) {
      this.flow.cancel();
      this.refreshTransfers();
    }
  }

  getIds() {
    return this._fileService.getFileIds();
  }

  ngOnDestroy() {
    if (this.flow) {
      this.flow.cancel();
      AppFileInputComponent.FLOW_EVENT_NAMES.forEach(name => this.flow.off(name));
    }
    this.transfers$.complete();
  }
}
interface Dimensions {
  width: number;
  height: number;
}
type Orientation = 'vertical' | 'horizontal';
